import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'motion/react';
import { Send, CheckCircle2, Sparkles, Zap, ChevronRight } from 'lucide-react';
import VoiceRecorder from '../components/VoiceRecorder';
import LiveYhuuAi from '../components/LiveYhuuAi';
import { LoyaltyTest, Answer } from '../types';
import { useTheme } from '../lib/ThemeContext';
import { domToBlob } from 'modern-screenshot';
import { track } from '../lib/analytics';
import ReportYhuu from '../components/ReportYhuu';
import GuessWho from '../components/GuessWho';
import FollowUpToggle from '../components/FollowUpToggle';


// Turns any URL inside a block of text into a real clickable link,
// leaving everything else as plain text. Used anywhere a creator's
// raw question text (which may contain a pasted link) is shown.
const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const URL_TEST_REGEX = /^https?:\/\/[^\s]+$/;
function Linkify({ text }: { text: string }) {
  if (!text) return null;
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) =>
        URL_TEST_REGEX.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="underline break-all"
            style={{ color: 'inherit', textDecorationColor: 'currentColor' }}
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

// Quick replies a recipient can tap instead of typing. Tapping one fills the
// answer box (still editable) — they press Next to send it.
const REPLY_TEMPLATES = [
  'I will reply if you tell me who you are',
  'Who is this? 👀',
  'I think I know who you are 😏',
];

function CountUp({ from, to, className }: { from: number; to: number; className?: string }) {
  const [count, setCount] = useState(from);
  useEffect(() => {
    const duration = 1800;
    const steps = 40;
    const increment = (to - from) / steps;
    let current = from;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current += increment;
      setCount(step >= steps ? to : Math.round(current));
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [from, to]);
  return <span className={className}>{count.toLocaleString()}</span>;
}

export default function TestView() {
  const { testId } = useParams<{ testId: string }>();
  const { theme } = useTheme();
  const light = theme === 'light';
  const [test, setTest] = useState<LoyaltyTest | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [tempAnswer, setTempAnswer] = useState('');
  const [tempAnswerAudioUrl, setTempAnswerAudioUrl] = useState<string | null>(null);
  const [answerMode, setAnswerMode] = useState<'text' | 'voice'>('text');
  const [showReply, setShowReply] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [retakeCount, setRetakeCount] = useState<number>(0);
  const [isPremiumCreator, setIsPremiumCreator] = useState(false);
  const [retakeBlocked, setRetakeBlocked] = useState(false);

  const [shareToast, setShareToast] = useState('');
  const [liveCount, setLiveCount] = useState(500);
  const [isCapturing, setIsCapturing] = useState(false);
  const shareCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const pool = [121, 450, 35, 100, 256, 78];
    setLiveCount(pool[Math.floor(Math.random() * pool.length)]);
    const interval = setInterval(() => {
      setLiveCount(pool[Math.floor(Math.random() * pool.length)]);
    }, 300000);
    return () => clearInterval(interval);
  }, []);

  const stepStartTime = useRef<number>(Date.now());
  const editCount = useRef<number>(0);
  const lastValue = useRef<string>('');
  const replyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    stepStartTime.current = Date.now();
    editCount.current = 0;
    lastValue.current = '';
    setShowReply(false);
  }, [currentStep]);

  useEffect(() => {
    const fetchTest = async () => {
      if (!testId) return;
      try {
        const { data, error } = await supabase.from('tests').select('*').eq('id', testId).single();
        if (error) {
          // Not a real Yhuu session (or already deleted). Rather than show
          // a broken empty "completed" screen, forward to the separate
          // Yhuu Test quiz app — this /test/:id path used to be proxied
          // there for every request by a Vercel rewrite; now that the
          // rewrite only matches real sessions get through here, and
          // anything that isn't a real session falls back to the quiz app
          // the same way it always used to.
          window.location.replace(
            `https://views-project-git-main-edwinafriyie16-8940s-projects.vercel.app/test/${testId ?? ''}${window.location.search}`
          );
          return;
        }
        if (data) {
          track('recipient_open', { status: data.status }, testId);
          // Fetch retake count
          setRetakeCount(data.retake_count ?? 0);
          // Check if creator is premium
          if (data.creator_id) {
            const { data: profile } = await supabase.from('profiles').select('is_premium, premium_until').eq('id', data.creator_id).maybeSingle();
            const premium = profile?.is_premium || (profile?.premium_until && new Date(profile.premium_until) > new Date());
            setIsPremiumCreator(!!premium);
            const limit = premium ? Infinity : 2;
            if ((data.retake_count ?? 0) >= limit) setRetakeBlocked(true);
          }
          // Only show completed screen if answers actually exist
          const hasAnswers = data.answers && data.answers.length > 0;
          const retakeAllowed = false; // honesty ratings removed — nothing triggers a forced retake anymore
          // The sender sent their one follow-up question — it's appended to
          // `questions`, so there's now one more question than answers.
          // Send the recipient straight back into the answer flow for just
          // that one new question, instead of the completed screen.
          const hasPendingFollowUp =
            (data.questions?.length ?? 0) > (data.answers?.length ?? 0) && hasAnswers;
          if (data.status === 'completed' && hasAnswers && !retakeAllowed && !hasPendingFollowUp) {
            setTest(data);
            setAnswers(data.answers);
            setCompleted(true);
          } else {
            // Always show questions if no answers — reset stale status
            if (data.status === 'completed' && !hasAnswers) {
              await supabase.from('tests').update({ status: 'pending' }).eq('id', testId);
            }
            setTest({ ...data, status: 'pending' });
            if (hasPendingFollowUp) {
              // Resume exactly where they left off — their earlier answers
              // stay recorded, they only see/answer the new question.
              setAnswers(data.answers);
              setCurrentStep(data.answers.length);
            }
            if (!data.opened_at) {
              await supabase
                .from('tests')
                .update({ opened_at: new Date().toISOString() })
                .eq('id', testId);
              // Fire-and-forget: ask the server to push-notify the creator
              // that their session was opened. The Edge Function itself
              // decides — server-side — whether the creator has actually
              // paid for this (site Premium or a one-off unlock on this
              // test); it silently no-ops otherwise. Never blocks the
              // receiver's flow either way.
              supabase.functions.invoke('send-push', {
                body: { type: 'test_opened', testId },
              }).catch(() => {});
            }
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching test:', error);
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    if (newVal !== lastValue.current) {
      editCount.current += 1;
      lastValue.current = newVal;
    }
    setTempAnswer(newVal);
  };

  // skip = the recipient chose not to answer this question. It's saved as an
  // empty answer flagged `skipped`, and the sender sees "Skipped" on results.
  const handleNext = async (skip = false) => {
    if (!skip && !tempAnswer && !tempAnswerAudioUrl) return;
    if (!test) return;
    const responseTimeMs = Date.now() - stepStartTime.current;
    const isLastQuestion = currentStep === test.questions.length - 1;

    const answerEntry: Answer = skip
      ? {
          questionId: test.questions[currentStep].id,
          partnerAnswer: '',
          responseTimeMs,
          editCount: 0,
          answerLength: 0,
          skipped: true,
        }
      : {
          questionId: test.questions[currentStep].id,
          partnerAnswer: tempAnswer,
          responseTimeMs,
          editCount: editCount.current,
          answerLength: tempAnswer.length,
          ...(tempAnswerAudioUrl ? { audioUrl: tempAnswerAudioUrl } : {}),
        };
    if (skip) track('recipient_skipped', { step: currentStep }, testId);

    if (isLastQuestion) {
      const allAnswers = [...answers, answerEntry];
      setAnswers(allAnswers);
      setTempAnswer('');
      setTempAnswerAudioUrl(null);
      setAnswerMode('text');
      submitAnswers(allAnswers);
    } else {
      setAnswers([...answers, answerEntry]);
      setTempAnswer('');
      setTempAnswerAudioUrl(null);
      setAnswerMode('text');
      setCurrentStep(currentStep + 1);
    }
  };

  const submitAnswers = async (finalAnswers: Answer[]) => {
    if (!testId) return;
    setIsSubmitting(true);
    try {
      const isRetake = retakeCount > 0;
      const updatePayload: any = {
        answers: finalAnswers,
        status: 'completed',
        updated_at: new Date().toISOString(),
      };
      if (isRetake) {
        updatePayload.retake_count = retakeCount + 1;
        updatePayload.previous_answers = test?.answers ?? null;
      }
      const { error } = await supabase
        .from('tests')
        .update(updatePayload)
        .eq('id', testId);
      if (error) throw error;

      // Fire-and-forget: email + push the creator that their session was
      // answered. This one is free for everyone — see send-push's
      // test_answered type.
      supabase.functions.invoke('notify-creator', { body: { testId } }).catch(() => {});
      supabase.functions.invoke('send-push', { body: { type: 'test_answered', testId } }).catch(() => {});

      track('recipient_answered', { questions: finalAnswers.length }, testId);
      setCompleted(true);
    } catch (error) {
      console.error('Error submitting answers:', error);
      alert('Couldn\'t save your answers. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-10 h-10 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (completed || !test) {
    const shareQuestions = test?.questions ?? [];
    const shareAnswers = answers;
    const BG = 'linear-gradient(160deg, #7c3aed 0%, #db2777 45%, #f43f5e 100%)';
    const CARD_GRADIENT = 'linear-gradient(90deg, #e11d48, #fb7185)';

    // No preview screen, no second tap — the button itself is the share
    // action. The card renders off-screen (still in the DOM so it can be
    // captured) and the OS share sheet opens directly.
    const shareMystery = async () => {
      if (!shareCardRef.current || isCapturing) return;
      setIsCapturing(true);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      try {
        const blob = await domToBlob(shareCardRef.current, {
          scale: 2,
          backgroundColor: '#ffffff',
        });
        if (!blob) throw new Error('Failed to generate image');

        const file = new File([blob], 'yhuu-mystery.png', { type: 'image/png' });
        const captionText = "Someone sent me anonymous questions on Yhuu... who was it? 👀";

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'Yhuu', text: captionText }).catch(() => {});
        } else {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = 'yhuu-mystery.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
          setShareToast('Image downloaded!');
          setTimeout(() => setShareToast(''), 2500);
        }
      } catch (e) {
        console.error('Share error', e);
        setShareToast('Something went wrong');
        setTimeout(() => setShareToast(''), 2500);
      } finally {
        setIsCapturing(false);
      }
    };

    return (
      <>
        {/* Toast */}
        <AnimatePresence>
          {shareToast && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed top-6 left-1/2 -translate-x-1/2 bg-white text-zinc-900 px-5 py-3 rounded-full text-xs font-bold shadow-xl z-[60]"
            >
              {shareToast}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Off-screen render target for the share card — same NGL-style
            gradient-banner + white-question-panel grammar as the real
            question card, just re-purposed for "who sent this". Kept
            permanently mounted (not display:none, which breaks capture)
            so there's no preview step: tap the button, get the share sheet. */}
        <div style={{ position: 'fixed', top: 0, left: -9999, pointerEvents: 'none' }}>
          <div ref={shareCardRef} className="w-[390px] bg-white rounded-[28px] overflow-hidden">
            <div className="px-8 py-7 text-center" style={{ background: CARD_GRADIENT }}>
              <p className="text-white font-black text-2xl tracking-tight">WHO SENT ME THIS? 👀</p>
            </div>

            <div className="px-8 pt-10 pb-2">
              {shareQuestions.map((q, i) => {
                const ans = shareAnswers.find(a => a.questionId === q.id);
                const ansText = ans?.partnerAnswer ?? '...';
                return (
                  <div key={q.id} className={i > 0 ? 'mt-10 pt-8 border-t border-rose-100' : ''}>
                    <h3 className="text-3xl font-black leading-[1.25] tracking-tight text-zinc-900 text-center">
                      <Linkify text={q.text} />
                    </h3>
                    <div className="w-full bg-rose-50 rounded-2xl px-5 py-5 mt-6">
                      <p className="text-base font-bold text-gray-900 leading-snug blur-[6px] select-none text-center">
                        {ansText}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between px-8 py-6 mt-4">
              <span className="text-xs font-bold text-rose-400">yhuu.life</span>
              <span className="text-xs font-bold text-rose-600">Send Anonymously Now</span>
            </div>
          </div>
        </div>

        {/* ── MAIN COMPLETION SCREEN ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ background: BG }}
        >
          {/* Sudoku number background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <svg viewBox="0 0 390 844" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
              <g opacity="0.13" fill="none" stroke="white" strokeWidth="0.8">
                <rect x="220" y="-20" width="90" height="90" rx="4" transform="rotate(15,265,25)"/>
                <rect x="290" y="70" width="110" height="110" rx="4" transform="rotate(-8,345,125)"/>
                <rect x="-20" y="90" width="100" height="100" rx="4" transform="rotate(20,30,140)"/>
                <rect x="180" y="340" width="120" height="120" rx="4" transform="rotate(-12,240,400)"/>
                <rect x="-10" y="560" width="105" height="105" rx="4" transform="rotate(18,42,612)"/>
                <rect x="260" y="580" width="95" height="95" rx="4" transform="rotate(-5,307,627)"/>
              </g>
              <g opacity="0.12" fill="white" fontFamily="-apple-system,sans-serif" fontSize="14" fontWeight="600">
                <text x="230" y="22">3</text><text x="252" y="40">7</text><text x="274" y="22">1</text>
                <text x="310" y="95">4</text><text x="332" y="113">9</text><text x="354" y="95">2</text>
                <text x="12" y="125">6</text><text x="34" y="143">1</text><text x="56" y="125">8</text>
                <text x="48" y="230">5</text><text x="70" y="248">3</text>
                <text x="190" y="378">7</text><text x="212" y="396">2</text><text x="234" y="378">5</text>
                <text x="320" y="420">8</text><text x="342" y="438">1</text>
                <text x="12" y="595">9</text><text x="34" y="613">4</text>
                <text x="278" y="608">6</text><text x="300" y="626">3</text><text x="322" y="608">7</text>
                <text x="350" y="710">5</text><text x="372" y="728">1</text>
              </g>
            </svg>
          </div>

          <div className="relative z-10 flex flex-col min-h-screen">
            {/* Scrollable content */}
            <div className="flex flex-col items-center px-5 pt-10 pb-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-sm"
              >

                {/* Small "Sent" badge so the recipient knows their answer went through */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 20, delay: 0.1 }}
                  className="flex justify-center mb-4"
                >
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 18px 8px 10px', borderRadius: 999, background: '#fff', boxShadow: '0 6px 18px rgba(50,8,80,0.25)' }}>
                    <CheckCircle2 size={22} color="#10b981" strokeWidth={2.6} />
                    <span style={{ fontSize: 14, fontWeight: 900, color: '#18181b' }}>Sent</span>
                  </div>
                </motion.div>

                {/* Off by default — the recipient has to turn this on
                    themselves. Hidden entirely once the sender has already
                    used their one follow-up on this session. */}
                {testId && !test.follow_up_used && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mb-4"
                  >
                    <FollowUpToggle testId={testId} initialEnabled={!!test.follow_up_enabled} />
                  </motion.div>
                )}

                {/* Generates the blurred "WHO SENT ME THIS? 👀" share card and
                    opens the native share sheet directly — no preview step —
                    only reachable after answering, since it's the completed
                    answer that gets (blurred) shown off. */}
                {testId && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18 }}
                    className="mb-4"
                  >
                    <button
                      type="button"
                      onClick={shareMystery}
                      disabled={isCapturing}
                      data-track="recipient_share_mystery"
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                        width: '100%', padding: '13px 18px', borderRadius: 18, cursor: isCapturing ? 'default' : 'pointer',
                        fontFamily: 'inherit', textAlign: 'center', border: '1.5px solid #fbcfe0',
                        background: '#fff0f4', opacity: isCapturing ? 0.7 : 1,
                      }}
                    >
                      {isCapturing ? (
                        <div className="w-4 h-4 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <span style={{ fontSize: 14, fontWeight: 800, color: '#18181b' }}>
                            Find who sent this on your socials? 🔎
                          </span>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#be123c' }}>
                            Free hint: they're in your contacts 👀
                          </span>
                        </>
                      )}
                    </button>
                  </motion.div>
                )}

                {/* Same card as the answer screen, at the top: "Send Anonymously Now!" */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-3xl overflow-hidden shadow-2xl mb-2"
                >
                  <div
                    className="px-8 py-7 text-center"
                    style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)' }}
                  >
                    <p className="text-white font-black text-xl sm:text-2xl tracking-tight">
                      Send Anonymously Now!
                    </p>
                  </div>

                  <div className="bg-white px-8 pt-14 pb-8 text-center">
                    <h3 className="text-3xl sm:text-4xl font-black leading-[1.25] tracking-tight text-zinc-900">
                      Want to send something anonymously now? You don't need to wait for their link ngl 🔗
                    </h3>
                  </div>

                  <div className="bg-white px-8 pb-8">
                    <a
                      href="/"
                      data-track="recipient_send_my_yhuu"
                      className="w-full font-black text-base py-5 rounded-full flex items-center justify-center gap-2 text-white transition-transform active:scale-[0.97]"
                      style={{ background: 'linear-gradient(90deg, #e11d48, #ec4899)' }}
                    >
                      Send my Yhuu Now
                    </a>
                  </div>
                </motion.div>

                {/* Social proof */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.12 }}
                  className="flex items-center justify-center gap-2 py-3"
                >
                  <div className="flex">
                    {['#a855f7','#ec4899','#f43f5e','#8b5cf6'].map((c, i) => (
                      <div key={i} className="w-7 h-7 rounded-full border-2 border-white/40 flex items-center justify-center text-white text-[10px] font-black" style={{ background: c, marginLeft: i > 0 ? '-8px' : '0' }}>
                        {['A','K','T','M'][i]}
                      </div>
                    ))}
                  </div>
                  <span className="text-white/70 text-sm font-semibold">
                    <CountUp from={liveCount - 20} to={liveCount} className="text-white font-black" /> <span className="text-white font-black">people</span> answered today
                  </span>
                </motion.div>

                {test && testId && (
                  <div className="text-center pt-1">
                    <ReportYhuu testId={testId} tone="onGradient" />
                  </div>
                )}

              </motion.div>
            </div>

            {/* Bottom sudoku fill — expands to fill remaining screen */}
            <div className="flex-1 w-full overflow-hidden" style={{ minHeight: '160px' }}>
              <svg viewBox="0 0 390 220" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
                <g opacity="0.16" fill="none" stroke="white" strokeWidth="0.9">
                  <rect x="8" y="15" width="88" height="88" rx="4" transform="rotate(-8,52,59)"/>
                  <rect x="105" y="45" width="72" height="72" rx="4" transform="rotate(12,141,81)"/>
                  <rect x="200" y="5" width="92" height="92" rx="4" transform="rotate(-5,246,51)"/>
                  <rect x="295" y="30" width="80" height="80" rx="4" transform="rotate(9,335,70)"/>
                  <rect x="45" y="108" width="68" height="68" rx="4" transform="rotate(15,79,142)"/>
                  <rect x="155" y="118" width="75" height="75" rx="4" transform="rotate(-10,192,155)"/>
                  <rect x="268" y="105" width="82" height="82" rx="4" transform="rotate(6,309,146)"/>
                </g>
                <g opacity="0.15" fill="white" fontFamily="-apple-system,sans-serif" fontSize="13" fontWeight="600">
                  <text x="15" y="38">5</text><text x="35" y="54">1</text><text x="55" y="38">9</text>
                  <text x="112" y="68">4</text><text x="132" y="84">7</text><text x="152" y="68">2</text>
                  <text x="208" y="28">8</text><text x="228" y="44">3</text><text x="248" y="28">6</text>
                  <text x="302" y="52">2</text><text x="322" y="68">9</text>
                  <text x="52" y="128">3</text><text x="72" y="144">6</text>
                  <text x="162" y="140">1</text><text x="182" y="156">5</text><text x="202" y="140">4</text>
                  <text x="275" y="128">7</text><text x="295" y="144">2</text><text x="315" y="128">8</text>
                  <text x="355" y="95">3</text><text x="372" y="111">6</text>
                </g>
              </svg>
            </div>
          </div>
        </motion.div>
      </>
    );
  }

  const currentQuestion = test.questions[currentStep];
  // True only when we're looking at the one-and-only follow-up question:
  // it's the newest item, everything before it is already answered, and
  // the sender has actually used their one shot. Without this, a returning
  // recipient would see "Sup" with zero context that it's a continuation.
  const isFollowUpQuestion = !!test.follow_up_used && currentStep > 0 && answers.length === currentStep;

  return (
    <div className="max-w-2xl mx-auto min-h-[85dvh] flex flex-col justify-center py-8 px-4">
      <p
        className="text-center text-sm font-semibold mb-4"
        style={{ color: light ? '#9d6b7a' : '#a1a1aa' }}
      >
        This message was sent through Yhuu
      </p>

      {isFollowUpQuestion && (
        <div className="rounded-2xl overflow-hidden mb-4 border" style={{ borderColor: light ? '#fbcfe0' : '#3f3f46' }}>
          <div className="px-5 py-2.5" style={{ background: light ? '#fff0f4' : '#27171d' }}>
            <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: '#e11d48' }}>
              🔁 They have one more question for you
            </p>
          </div>
          <div className="px-5 py-4 space-y-3" style={{ background: light ? '#fff' : '#18181b' }}>
            <p className="text-xs" style={{ color: light ? '#9d6b7a' : '#a1a1aa' }}>
              You already answered this — here's what you said before:
            </p>
            {answers.map((a, i) => {
              const q = test.questions.find(q => q.id === a.questionId);
              if (!q) return null;
              return (
                <div key={a.questionId ?? i} className="text-sm">
                  <p className="font-bold" style={{ color: light ? '#18181b' : '#f4f4f5' }}>{q.text || 'Voice question'}</p>
                  <p style={{ color: light ? '#71717a' : '#a1a1aa' }}>
                    {a.skipped ? '(skipped)' : (a.partnerAnswer || '…')}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="rounded-3xl overflow-hidden shadow-2xl mb-5"
        >
          {/* NGL-style card: gradient banner + white question panel + action buttons */}
          <div
            className="px-8 py-7 text-center"
            style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)' }}
          >
            <p className="text-white font-black text-xl sm:text-2xl tracking-tight">
              {isFollowUpQuestion ? 'One More Thing…' : 'Send Anonymously Now!'}
            </p>
          </div>

          <div className="bg-white px-8 pt-14 pb-11 text-center">
            <h3 className="text-3xl sm:text-4xl font-black leading-[1.25] tracking-tight text-zinc-900">
              {currentQuestion.text ? <Linkify text={currentQuestion.text} /> : 'Listen to the voice question below'}
            </h3>

            {currentQuestion.audioUrl && (
              <audio src={currentQuestion.audioUrl} controls className="w-full mt-6" />
            )}
          </div>

          <div className="bg-white px-8 pb-8 space-y-4">
            <button
              type="button"
              onClick={() => {
                setShowReply(true);
                requestAnimationFrame(() =>
                  replyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                );
              }}
              className="w-full font-black text-base py-5 rounded-full flex items-center justify-center gap-2 transition-transform active:scale-[0.97]"
              style={{ background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff' }}
            >
              <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.28)' }}>
                <span className="font-bold text-sm leading-none" style={{ color: '#fff' }}>Y</span>
              </span>
              Reply
            </button>

            <button
              type="button"
              onClick={() => handleNext(true)}
              disabled={isSubmitting}
              data-track="recipient_skip_question"
              className="w-full text-sm font-bold py-2 text-zinc-500 hover:text-zinc-700 transition-colors disabled:opacity-50"
            >
              Skip question
            </button>
          </div>
        </motion.div>

        <p
          className="text-center text-sm font-semibold mb-5 -mt-1"
          style={{ color: light ? '#9d6b7a' : '#a1a1aa' }}
        >
          Reply honestly
        </p>

        {testId && (
          <div className="text-center mb-5">
            <GuessWho testId={testId} />
          </div>
        )}

        {showReply && (
        <motion.div
          key={`reply-${currentStep}`}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          ref={replyRef}
          className="bg-brand-card border border-brand-border rounded-3xl p-6 shadow-2xl mb-5 scroll-mt-4"
        >
          <div className="space-y-4">
            {/* WhatsApp-style quoted reply preview — stays pinned to the
                top of the screen while typing, so the question never
                disappears behind the keyboard. */}
            <div
              className="sticky top-2 z-20 flex items-start gap-3 rounded-xl px-3 py-2.5 border-l-4 border-brand-accent shadow-md"
              style={{ background: light ? '#fdf0f4' : '#1c1418' }}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-brand-accent mb-0.5">Replying to Anonymous</p>
                <p
                  className="text-sm truncate"
                  style={{ color: light ? '#7a4f5e' : '#a1a1aa' }}
                >
                  {currentQuestion.text || 'Voice question'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowReply(false)}
                aria-label="Cancel reply"
                className="text-lg leading-none shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                style={{ color: light ? '#7a4f5e' : '#a1a1aa' }}
              >
                ×
              </button>
            </div>

            <div className="flex gap-2 mb-1">
              <button
                type="button"
                onClick={() => setAnswerMode('text')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                  answerMode === 'text' ? 'bg-brand-accent text-white' : 'bg-transparent border border-brand-border text-zinc-400'
                }`}
              >
                Type
              </button>
              <button
                type="button"
                onClick={() => setAnswerMode('voice')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                  answerMode === 'voice' ? 'bg-brand-accent text-white' : 'bg-transparent border border-brand-border text-zinc-400'
                }`}
              >
                Record
              </button>
            </div>

            {test.live_ai_enabled && testId && (
              <LiveYhuuAi
                testId={testId}
                questionId={currentQuestion.id}
                questionText={currentQuestion.text || 'Please answer the voice question.'}
                questionAudioUrl={currentQuestion.audioUrl}
                light={light}
                onSaved={(audioUrl, transcript) => {
                  setTempAnswer(transcript);
                  setTempAnswerAudioUrl(audioUrl);
                  setAnswerMode('voice');
                }}
              />
            )}

            {answerMode === 'text' && (
              <div className="pt-1">
                <p className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: light ? '#9d6b7a' : '#a1a1aa' }}>Quick replies</p>
                <div className="flex flex-wrap gap-2">
                  {REPLY_TEMPLATES.map(t => {
                    const on = tempAnswer === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        data-track="reply_template"
                        onClick={() => { editCount.current += 1; lastValue.current = t; setTempAnswer(t); }}
                        className="text-left text-[13px] font-bold rounded-2xl px-3.5 py-2 transition-colors"
                        style={{
                          border: on ? '2px solid #e11d48' : `1.5px solid ${light ? '#f3d3de' : '#3f2a33'}`,
                          background: on ? (light ? '#fff0f4' : 'rgba(225,29,72,0.12)') : 'transparent',
                          color: on ? '#be123c' : (light ? '#7a4f5e' : '#d4d4d8'),
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {answerMode === 'text' ? (
              <textarea
                value={tempAnswer}
                onChange={handleTextChange}
                onFocus={() => {
                  // iOS re-scrolls the page when the keyboard opens, which
                  // can shove the quoted question and top card off-screen.
                  // Nudge back to a resting position once the keyboard
                  // animation has settled.
                  setTimeout(() => {
                    replyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }, 350);
                }}
                placeholder="Be real with it..."
                rows={4}
                style={{
                  background: light ? '#fff0f5' : 'rgba(13,13,14,0.5)',
                  borderColor: light ? '#f0d6e0' : undefined,
                  color: light ? '#1a0a0f' : '#ffffff',
                }}
                className="w-full border border-brand-border rounded-2xl px-4 py-4 focus:outline-none focus:border-brand-accent transition-all text-lg resize-none placeholder:text-zinc-400"
              />
            ) : tempAnswerAudioUrl ? (
              <div className="flex items-center gap-3">
                <audio src={tempAnswerAudioUrl} controls className="flex-1" />
                <button
                  type="button"
                  onClick={() => setTempAnswerAudioUrl(null)}
                  className="text-xs font-bold uppercase tracking-wide text-brand-accent"
                >
                  Redo
                </button>
              </div>
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-widest -mt-1 mb-1" style={{ color: light ? '#9d6b7a' : '#71717a' }}>
                  Your real voice will be sent, unfiltered
                </p>
                <VoiceRecorder
                  pathPrefix={`answers/${testId}-${currentQuestion.id}`}
                  onRecorded={(url) => setTempAnswerAudioUrl(url)}
                  disguise={false}
                />
              </>
            )}

            <button
              onClick={() => handleNext()}
              disabled={(!tempAnswer && !tempAnswerAudioUrl) || isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-xs font-bold py-4"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {currentStep === test.questions.length - 1 ? 'Send' : 'Next Question →'}
                  <Send className="w-4 h-4 ml-1 opacity-50" />
                </>
              )}
            </button>
          </div>
        </motion.div>
        )}
      </AnimatePresence>

      {/* Report — for recipients who don't want this or feel unsafe */}
      {testId && (
        <div className="text-center -mt-1">
          <ReportYhuu testId={testId} />
        </div>
      )}

    </div>
  );
}
